import { http } from '@/core/api';
import { executeRequest } from '@/core/either';
import type { ApiResult } from '@/core/either';
import type { ITermsCard, ITermsCardCheck } from '@/types/card';
import type { ITermsConditions, BusinessProcess } from '@/shared/onboardingForms/types/termsCondition.type';

/**
 * Servicio compartido para operaciones relacionadas con términos y condiciones.
 * Usado por módulos: card, onboarding, dashboard
 */
export const termsService = {
  /**
   * Obtiene los términos y condiciones de la tarjeta.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ITermsCard[]>).
   */
  getTermsAndConditions(): Promise<ApiResult<ITermsCard[]>> {
    return executeRequest(() =>
      http.get<ITermsCard[]>('card/b2b/terms-and-conditions')
    );
  },

  /**
   * Actualiza la aceptación de términos y condiciones de la tarjeta.
   * @param migration - Indica si es una migración.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<void>).
   */
  updateTermsAcceptance(migration = false): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>('card/b2b/terms-and-conditions/accept', {
        migration
      })
    );
  },

  /**
   * Obtiene el estado de aceptación de los términos y condiciones.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ITermsCardCheck>).
   */
  getTermsAcceptanceStatus(): Promise<ApiResult<ITermsCardCheck>> {
    return executeRequest(() =>
      http.get<ITermsCardCheck>('card/b2b/terms-and-conditions/accept')
    );
  },

  /**
   * Obtiene los términos y condiciones corporativos por país.
   * Usado por módulos onboarding y card para términos corporativos.
   * @param countryCode - Código del país (ej: 'CL', 'CO', 'AR').
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ITermsConditions[]>).
   */
  getCompanyTerms(countryCode: string): Promise<ApiResult<ITermsConditions[]>> {
    return executeRequest(() =>
      http.get<ITermsConditions[]>(`company/b2b/terms/${countryCode}`)
    );
  },

  /**
   * Confirma la aceptación de términos y condiciones corporativos.
   * Usado por módulos onboarding y card tras aceptar términos.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<void>).
   */
  postCompanyTerms(): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>('company/b2b/terms')
    );
  },

  /**
   * Obtiene los términos y condiciones asociados a un proceso de negocio
   * (ej: remuneración CLP, stablecoins).
   * @param businessProcess - Identificador del proceso de negocio.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ITermsConditions[]>).
   */
  getCompanyTermsByBusinessProcess(
    businessProcess: BusinessProcess
  ): Promise<ApiResult<ITermsConditions[]>> {
    return executeRequest(() =>
      http.get<ITermsConditions[]>('company/b2b/terms-and-conditions', {
        params: { businessProcess },
      })
    );
  },
};